import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { fmtNumber, useT, type MessageKey } from '@/ui/i18n';
import { useDevices, type ConnState } from '@/store/devices';
import { useSettings } from '@/store/settings';
import { useAccount } from '@/store/account';
import { needsFtp, resolveDisplayName } from '@/store/settingsModel';
import { ErrorBoundary } from './ErrorBoundary';
import { OnboardingPage } from '@/ui/pages/OnboardingPage';

/** 16px outline icons, one set (1.6px stroke via CSS). */
const ICONS: Record<string, ReactNode> = {
  home: <path d="M3 10.5 12 3l9 7.5M5.5 9v12h13V9" />,
  ride: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m10 8.5 5.5 3.5-5.5 3.5z" />
    </>
  ),
  devices: <path d="m7 7 10 10-5 5V2l5 5L7 17" />,
  workouts: <path d="M3 12h4l3-7 4 14 3-7h4" />,
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  routes: (
    <>
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M8 19h8.5a3.5 3.5 0 0 0 0-7h-9a3.5 3.5 0 0 1 0-7H16" />
    </>
  ),
  rides: (
    <>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.5 8.5" />
      <path d="M3.5 3.5v5h5M12 7.5V12l3 2.5" />
    </>
  ),
  group: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20.5a6.5 6.5 0 0 1 13 0M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14.2a6.5 6.5 0 0 1 3.5 6.3" />
    </>
  ),
  settings: (
    <>
      <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
      <circle cx="15" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="18" r="2" />
    </>
  ),
  debug: <path d="m5 8 4 4-4 4M12 16h7" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
};

function Icon({ name }: { name: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      {ICONS[name]}
    </svg>
  );
}

const train: { to: string; key: MessageKey; icon: string; end?: boolean }[] = [
  { to: '/', key: 'nav.home', icon: 'home', end: true },
  { to: '/fahren', key: 'nav.ride', icon: 'ride' },
  { to: '/workouts', key: 'nav.workouts', icon: 'workouts' },
  { to: '/kalender', key: 'nav.calendar', icon: 'calendar' },
  { to: '/routen', key: 'nav.routes', icon: 'routes' },
  { to: '/fahrten', key: 'nav.rides', icon: 'rides' },
  { to: '/gruppenfahrt', key: 'nav.group', icon: 'group' },
];
const setup: typeof train = [
  { to: '/geraete', key: 'nav.devices', icon: 'devices' },
  { to: '/einstellungen', key: 'nav.settings', icon: 'settings' },
];

function stateClass(s: ConnState): string {
  return s === 'connected' ? 'on' : s === 'connecting' || s === 'reconnecting' ? 'busy' : '';
}

/** Live device status: what the rider needs to know before starting. */
function DeviceStatus() {
  const t = useT();
  const trainer = useDevices((s) => s.trainer);
  const hr = useDevices((s) => s.hr);
  const click = useDevices((s) => s.click);
  const clickOn = useSettings((s) => s.settings.clickEnabled);
  const label = (st: ConnState, name: string | undefined, fallback: string) =>
    st === 'connected'
      ? (name ?? fallback)
      : st === 'connecting' || st === 'reconnecting'
        ? `${fallback} · ${t('common.connecting')}`
        : `${fallback} · ${t('topbar.notPaired')}`;
  return (
    <Link to="/geraete" className="device-status" data-testid="topbar-devices">
      <span className={stateClass(trainer.state)}>
        <i className="dot" />
        {label(trainer.state, trainer.name, t('topbar.trainer'))}
      </span>
      <span className={stateClass(hr.state)}>
        <i className="dot" />
        {label(hr.state, hr.name, t('topbar.hr'))}
      </span>
      {clickOn && (
        <span className={stateClass(click.state)}>
          <i className="dot" />
          {label(click.state, click.name, 'Zwift Click')}
        </span>
      )}
    </Link>
  );
}

function RiderSwitch() {
  const local = useSettings((s) => s.settings.displayName);
  const color = useSettings((s) => s.settings.riderColor);
  const ftp = useSettings((s) => s.settings.ftp);
  const kg = useSettings((s) => s.settings.riderWeightKg);
  const account = useAccount((s) => s.me?.name);
  const name = resolveDisplayName(local, account) || 'Gast';
  return (
    <Link to="/einstellungen" className="rider-switch" data-testid="topbar-rider">
      <span className="avatar" style={{ background: color }} aria-hidden>
        {name.slice(0, 1).toUpperCase()}
      </span>
      <span className="who">
        {name}
        <small className="num">
          FTP {ftp} W · {fmtNumber(ftp / kg, 1)} W/kg
        </small>
      </span>
    </Link>
  );
}

function Brand() {
  return (
    <Link to="/" className="brand" aria-label="VeloStube – Start">
      <img src="/icons/icon-64.png" alt="" width={32} height={32} />
      VeloStube
    </Link>
  );
}

/**
 * The FTP is asked exactly once. While account status / first sync are pending we show a splash, so a rider
 * logging in on a new device gets their synced FTP instead of the question.
 */
function useFtpGate(): 'ready' | 'wait' | 'ask' {
  const needs = useSettings((s) => needsFtp(s.settings));
  const status = useAccount((s) => s.status);
  const firstSyncDone = useAccount((s) => s.lastSyncAt !== null || s.syncError !== null);
  if (!needs) return 'ready';
  if (status === 'unknown' || (status === 'loggedIn' && !firstSyncDone)) return 'wait';
  return 'ask';
}

export function Layout() {
  const t = useT();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const gate = useFtpGate();
  useEffect(() => setMenuOpen(false), [pathname]);

  if (gate === 'wait') return <div className="splash">{t('common.loading')}</div>;
  if (gate === 'ask') return <OnboardingPage />;

  const item = (it: (typeof train)[number]) => (
    <NavLink key={it.to} to={it.to} end={it.end} className="nav-item">
      <Icon name={it.icon} />
      {t(it.key)}
    </NavLink>
  );
  return (
    <div className="shell">
      <nav className={`nav${menuOpen ? ' open' : ''}`} aria-label="Hauptnavigation">
        <Brand />
        {train.map(item)}
        <div className="nav-group">{t('nav.setup')}</div>
        {setup.map(item)}
        <div className="spacer" />
        <NavLink to="/debug/ble" className="nav-item">
          <Icon name="debug" />
          {t('nav.debug')}
        </NavLink>
      </nav>
      <header className="topbar">
        <button
          className="ghost menu-toggle"
          aria-label={t('nav.menu')}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
            {ICONS.menu}
          </svg>
        </button>
        <Brand />
        <DeviceStatus />
        <div className="spacer" />
        <RiderSwitch />
      </header>
      <main className="main">
        <div className="page">
          <ErrorBoundary resetKey={pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
